import { useEffect, useRef, useState } from 'react';
import { ACCENT, ACCENT_GLOW, STORY } from './landingData';
import type { StoryTone } from './landingData';

const PIXEL = "'Press Start 2P', monospace";
const VT = "'VT323', monospace";

/**
 * Per-tone type treatment. The long lines are all VT323 on purpose: Press
 * Start 2P advances a full em per glyph, so a 43-character line would be
 * ~1000px wide and overflow on every viewport. The pixel font is reserved
 * for the short slams.
 */
const TONE: Record<StoryTone, React.CSSProperties> = {
  slam:   { fontFamily: PIXEL, fontSize: 'clamp(20px, 5vw, 30px)', lineHeight: 1.5, letterSpacing: -1, color: '#f2ecdc' },
  body:   { fontFamily: VT, fontSize: 'clamp(19px, 3.4vw, 23px)', lineHeight: 1.34, color: '#8f9f94' },
  quote:  { fontFamily: VT, fontSize: 'clamp(24px, 5vw, 32px)', lineHeight: 1.2, color: '#e8ddc7' },
  beat:   { fontFamily: VT, fontSize: 'clamp(21px, 4vw, 26px)', lineHeight: 1.25, color: ACCENT },
  punch:  { fontFamily: PIXEL, fontSize: 'clamp(15px, 3.4vw, 20px)', lineHeight: 1.6, letterSpacing: -0.5, color: '#f2ecdc' },
  finale: { fontFamily: VT, fontSize: 'clamp(23px, 4.6vw, 31px)', lineHeight: 1.24, color: '#d8e2d9' },
};

/**
 * Blank space above each block, in vh so the rhythm scales with the screen.
 * These are what make the reveal scroll-driven: at ~58vh of gaps the band
 * runs past one viewport, so blocks arrive one at a time instead of all
 * being on screen at once with nothing left to reveal.
 */
const GAP: Record<StoryTone, number> = {
  slam: 0, body: 9, quote: 11, beat: 11, punch: 13, finale: 5,
};

/**
 * The skull is the punchline of its line, but at VT323's x-height it renders
 * as a speck. Give it its own size and the caution tint the rest of the page
 * uses for gates.
 */
function renderLine(line: string) {
  if (!line.includes('☠')) return line;
  const [before, after] = line.split('☠');
  return (
    <>
      {before}
      <span style={{ fontSize: '1.45em', lineHeight: 0, color: '#e3a53f' }}>☠</span>
      {after}
    </>
  );
}

/**
 * THE LOOP — the diagnosis band under the hero.
 *
 * Reads as a terminal boot sequence printed by the scroll: each block fades
 * up on its own as it rises into the top three-quarters of the viewport, so
 * the founder is fed one line at a time rather than handed a wall.
 *
 * One observer, every block a target. `rootMargin`'s -26% bottom inset is
 * what puts the trigger line above the fold instead of at it — a block that
 * revealed the instant its first pixel appeared would already be lit by the
 * time the reader's eye got there.
 *
 * Note the scroll container is a div (the page is `height:100dvh;
 * overflow-y:auto`), not the window — that is fine, a null root still
 * measures against the viewport and the blocks do move through it.
 */
export default function StoryBand() {
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const [shown, setShown] = useState<Set<number>>(new Set());

  useEffect(() => {
    const nodes = refs.current.filter((n): n is HTMLDivElement => n !== null);
    // No IntersectionObserver (jsdom, very old browsers) → show everything
    // rather than leaving the whole band invisible.
    if (!nodes.length || typeof IntersectionObserver === 'undefined') {
      setShown(new Set(STORY.map((_, i) => i)));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const arrived = entries
          .filter((e) => e.isIntersecting)
          .map((e) => Number((e.target as HTMLElement).dataset.i));
        if (!arrived.length) return;
        // Reveal is one-way: scrolling back up must not un-print the band.
        setShown((prev) => {
          const next = new Set(prev);
          for (const i of arrived) next.add(i);
          return next;
        });
        for (const e of entries) if (e.isIntersecting) io.unobserve(e.target);
      },
      { rootMargin: '0px 0px -26% 0px', threshold: 0.01 },
    );
    for (const n of nodes) io.observe(n);
    return () => io.disconnect();
  }, []);

  return (
    <section id="loop" style={{ borderTop: '1px solid #16241c', borderBottom: '1px solid #16241c', background: 'linear-gradient(180deg, #070c09, #0a120d)', padding: '16vh 0 18vh' }}>
      <div className="ld-story" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 34px', textAlign: 'center' }}>
        {STORY.map((block, i) => (
          <div
            key={i}
            data-i={i}
            ref={(el) => { refs.current[i] = el; }}
            className={`ld-story-block${shown.has(i) ? ' is-in' : ''}`}
            style={{ ...TONE[block.tone], marginTop: `${GAP[block.tone]}vh`, maxWidth: 840, marginLeft: 'auto', marginRight: 'auto' }}
          >
            {block.lines.map((line, j) => {
              // The closing line is the payoff of the whole page — it gets the
              // accent and the glow, nothing else in the band does.
              const isPayoff = block.tone === 'finale' && j === block.lines.length - 1;
              return (
                <div key={j} style={isPayoff ? { color: ACCENT, textShadow: `0 0 24px ${ACCENT_GLOW}` } : undefined}>
                  {renderLine(line)}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}
